/**
 * Ein Datum auswählen, in einem Blatt statt in einem Systemfenster.
 *
 * Vorher stand hier `window.prompt('Neues Fälligkeitsdatum (JJJJ-MM-TT):')`.
 * Das ist zweifach schlecht: das Fenster sieht in jedem Browser anders aus
 * und lässt sich nicht bedienen wie der Rest der Anwendung, und wer ein
 * Datum tippen muss, tippt es im deutschen Format – „15.04.2027" – und
 * bekommt dann eine Absage, die er nicht versteht.
 *
 * `input type="date"` wäre der naheliegende Ersatz gewesen, ist aber
 * nachgemessen ungeeignet: es stellt das Datum in der Sprache des
 * **Browsers** dar. Im Test stand dort „09/10/2026" für den 10. September.
 * Wer seinen Browser auf Englisch hat, sieht das auch im Betrieb, und bei
 * einer Prüffrist ist die Verwechslung von Tag und Monat kein
 * Schönheitsfehler. Deshalb der eigene Kalender in `Kalender.tsx`.
 *
 * Drei Wege zum selben Ziel, weil verschiedene Leute verschieden arbeiten:
 * tippen im deutschen Format, im Kalender klicken, oder springen. Die
 * Sprünge sind der häufigste Fall – eine Fälligkeit wird selten auf einen
 * bestimmten Tag verschoben, sondern „um einen Monat".
 */

import { useState } from 'react'
import { Blatt } from './Blatt'
import { Fehlerhinweis, datumLang } from './Hinweise'
import { Kalender, ausDeutsch, nachDeutsch } from './Kalender'

/** Verschiebt ein ISO-Datum um Tage oder Monate. */
function verschoben(iso: string, tage = 0, monate = 0): string {
  const [jahr, monat, tag] = iso.slice(0, 10).split('-').map(Number)
  // Mittags, nicht um Mitternacht: sonst kippt die Zeitzone das Datum um
  // einen Tag, und aus dem 1. März wird der 28. Februar.
  const d = new Date(jahr, monat - 1, tag, 12)
  d.setMonth(d.getMonth() + monate)
  d.setDate(d.getDate() + tage)
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-')
}

const SPRUENGE: { text: string; tage?: number; monate?: number }[] = [
  { text: '+ 1 Woche', tage: 7 },
  { text: '+ 2 Wochen', tage: 14 },
  { text: '+ 1 Monat', monate: 1 },
  { text: '+ 3 Monate', monate: 3 },
  { text: '+ 6 Monate', monate: 6 },
  { text: '+ 1 Jahr', monate: 12 },
]

export function Datumwahl({
  titel,
  untertitel,
  wert,
  hinweis,
  laeuft = false,
  fehler,
  beimWaehlen,
  schliessen,
}: {
  titel: string
  untertitel?: string
  /** Ausgangsdatum als ISO. Die Sprünge rechnen von hier. */
  wert: string
  hinweis?: string
  laeuft?: boolean
  fehler?: unknown
  beimWaehlen: (iso: string) => void
  schliessen: () => void
}) {
  const [gewaehlt, setzeGewaehlt] = useState(wert.slice(0, 10))
  /* Das Textfeld führt seinen eigenen Inhalt.
   *
   * Sonst könnte man es nicht bearbeiten: bei „15." wäre die Eingabe noch
   * kein gültiges Datum, ein abgeleiteter Wert würde sie zurücksetzen, und
   * das Feld ließe sich nicht mehr tippen. */
  const [getippt, setzeGetippt] = useState(nachDeutsch(wert.slice(0, 10)))
  const geaendert = gewaehlt !== wert.slice(0, 10)

  function ausKalender(iso: string) {
    setzeGewaehlt(iso)
    setzeGetippt(nachDeutsch(iso))
  }

  return (
    <Blatt
      titel={titel}
      untertitel={untertitel}
      schliessen={schliessen}
      fuss={
        <div className="flex gap-2">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf-haupt flex-1"
            disabled={laeuft || !gewaehlt || !geaendert}
            onClick={() => beimWaehlen(gewaehlt)}
          >
            {laeuft ? 'Speichert …' : 'Übernehmen'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {hinweis && <p className="text-base text-slate-700">{hinweis}</p>}

        <div>
          <label htmlFor="datumwahl">Neues Datum</label>
          <input
            id="datumwahl"
            inputMode="numeric"
            placeholder="TT.MM.JJJJ"
            value={getippt}
            aria-describedby="datumwahl-hinweis"
            aria-invalid={getippt !== '' && ausDeutsch(getippt) === ''}
            onChange={(e) => {
              setzeGetippt(e.target.value)
              const iso = ausDeutsch(e.target.value)
              if (iso) setzeGewaehlt(iso)
            }}
            style={{ minHeight: '56px' }}
          />
          {getippt !== '' && ausDeutsch(getippt) === '' && (
            <p id="datumwahl-hinweis" className="mt-1 text-base text-stufe-3">
              Bitte als Tag.Monat.Jahr eintragen, etwa 15.04.2027.
            </p>
          )}
        </div>

        <Kalender wert={gewaehlt} beimWaehlen={ausKalender} />

        <div>
          <p className="mb-1 font-semibold text-slate-800">Oder verschieben um</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {SPRUENGE.map((s) => {
              const ziel = verschoben(wert, s.tage, s.monate)
              return (
                <button
                  key={s.text}
                  type="button"
                  aria-pressed={gewaehlt === ziel}
                  className={`knopf-still px-2 text-base ${
                    gewaehlt === ziel ? 'border-akzent-500 bg-akzent-grund' : ''
                  }`}
                  onClick={() => ausKalender(ziel)}
                >
                  {s.text}
                </button>
              )
            })}
          </div>
        </div>

        {/* Ausgeschrieben und mit Wochentag. Bei einer Frist ist es der
            Unterschied, ob sie auf einen Sonntag fällt. */}
        <p
          aria-live="polite"
          className="rounded-feld border-2 border-slate-200 bg-slate-50 p-3 text-base"
        >
          {gewaehlt ? (
            <>
              Neu fällig am{' '}
              <span className="font-bold">{datumLang(gewaehlt)}</span>
            </>
          ) : (
            'Kein Datum gewählt.'
          )}
        </p>

        <Fehlerhinweis fehler={fehler} />
      </div>
    </Blatt>
  )
}
